import { curry, partial, replace } from "ramda";

// ResizeImageOptions is { width: Number, height: Number, quality: Number, maintainProportions: Boolean, format: String }
// ResizeImageBatch is { width: Number, height: Number, quality: Number, format: String, image: Image }

// String -> Promise[Image]
function loadImage(uri) {
    return new Promise(function (resolve, reject) {
        const img = new Image();

        img.src = uri;

        img.onload = partial(resolve, [img]);
        img.onerror = partial(reject, [new Error('Failed to load image.')]);
    });
}

// String -> Promise[Image]
async function loadImageBase64(base64) {
    try {
        return await loadImage(`data:;base64,${base64}`);
    } catch (e) {
        throw new Error('Encountered an error while loading the image. It may be an invalid or unsupported value.');
    }
}

// ({ width?: Number, height? Number }, { width: Number, height: Number}, Boolean) -> { width: Number, height: Number }
function adjustSize(targetSize, actualSize, maintainProportions) {
    const { width, height } = targetSize;
    const { width: actualWidth, height: actualHeight } = actualSize;

    if (width && height) {
        return { width, height };
    }

    if (height) {
        return {
            width: maintainProportions
                ? Math.max(actualWidth * (height / actualHeight), 1)
                : actualWidth,
            height,
        };
    }

    if (width) {
        return {
            width,
            height: maintainProportions
                ? Math.max(actualHeight * (width / actualWidth), 1)
                : actualHeight,
        };
    }

    return {
        width: actualWidth,
        height: actualHeight,
    };
}

function limitWidth(maxWidth, height, originalSize, maintainProportions) {
    return adjustSize(
        { width: Math.min(maxWidth, originalSize.width), height },
        originalSize,
        maintainProportions,
    );
}

const limitHeight = (width, maxHeight, originalSize, maintainProportions) =>
    adjustSize(
        { width, height: Math.min(maxHeight, originalSize.height) },
        originalSize,
        maintainProportions,
    );

function limitSize(
    { width, height, maxWidth, maxHeight },
    originalSize,
    maintainProportions,
) {
    if (maxWidth && maxHeight) {
        if (!maintainProportions) {
            return {
                width: Math.min(maxWidth, originalSize.width),
                height: Math.min(maxHeight, originalSize.height),
            };
        }

        const widthLimited = limitWidth(maxWidth, height, originalSize, maintainProportions);
        const heightLimited = limitHeight(width, maxHeight, originalSize, maintainProportions);

        return widthLimited.height <= maxHeight
            ? widthLimited
            : heightLimited;
    }

    if (maxWidth) {
        return limitWidth(maxWidth, height, originalSize, maintainProportions);
    }

    return limitHeight(width, maxHeight, originalSize, maintainProportions);
}

const determineDimensions = (options, image) => {
    const { maintainProportions } = options;

    if (options.maxWidth || options.maxHeight) {
        return limitSize(options, image, maintainProportions);
    }

    return adjustSize(options, image, maintainProportions);
}


// ResizeImageOptions -> Image -> ResizeImageBatch
const prepareBatch = curry((options, image) =>
    ({ ...options, ...determineDimensions(options, image), image })
);

// ResizeImageBatch -> String
function resizeImage({ width, height, format, quality, image }) {
    try {
        const cvs = document.createElement('canvas');
        cvs.width = width;
        cvs.height = height;

        const ctx = cvs.getContext('2d');
        ctx.drawImage(image, 0, 0, width, height);

        return cvs.toDataURL(format, quality);
    } catch (e) {
        throw new Error('Encountered an error while trying to alter the image: ' + e.message);
    }
};

// String -> String
const dataUrlToBase64 = replace(/^data:[^;]*;base64,(.*)$/i, '$1')

// ResizeImageOptions -> String -> String 
const resizeImageAsync = curry(function (options, base64) {
    return loadImageBase64(base64)
        .then(prepareBatch(options))
        .then(resizeImage)
        .then(dataUrlToBase64);
});

export {
    determineDimensions,
};

export default resizeImageAsync;